import React, { useCallback, useState, useMemo } from 'react'
import { createStyles, makeStyles, Theme, useTheme, withStyles } from '@material-ui/core/styles'
import { useTranslation, Trans } from 'react-i18next'
import {
	FormControl,
	Grid,
	IconButton,
	InputBase,
	MenuItem,
	Paper,
	Select,
	useMediaQuery,
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import CloseIcon from '@material-ui/icons/Close'
import InputLabel from '@material-ui/core/InputLabel'
import SearchIcon from '@material-ui/icons/Search'
import Chip from '@material-ui/core/Chip'
import { Close } from '@material-ui/icons'
import * as _ from 'lodash'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import { useQuery } from '@apollo/react-hooks'
import classNames from 'classnames'

import { IndustriesQuery, RoLocations } from 'store'
import CompanySearchSelect from '../../utils/selectComponent'
import styles from './styles'
import generalStyles from '../../../styles'
import { combineStyles } from 'utils'

const teamSize: { [key: string]: any } = {
	OneFive: '1-5',
	SixTen: '6-10',
	ElevenFifteen: '11-15',
	SixteenTwentyFive: '16-25',
	TwentySixFifty: '26-50',
	OverFifty: '50+',
}

const avgAge = ['20-29', '30-39', '40-49', '50+']
const BootstrapInput = withStyles((theme: Theme) =>
	createStyles({
		root: {
			'label + &': {
				marginTop: theme.spacing(2) - 5,
			}
		},
		input: {
			borderRadius: 2,
			position: 'relative',
			border: '1px solid #ebebeb',
			fontSize: 16,
			padding: '10px 26px 10px 12px',
			color: theme.palette.grey[600],
			transition: theme.transitions.create(['border-color', 'box-shadow']),
			fontFamily: 'Open Sans, sans-serif',
			'&:focus': {
				borderRadius: 2,
			},
		}
	}),
)(InputBase);

function getStyles(industryName: string, industry: string[], theme: Theme) {
	return {
		fontWeight:
			industry.indexOf(industryName) === -1
				? theme.typography.fontWeightRegular
				: theme.typography.fontWeightMedium,
	}
}

const combinedStyles: any = combineStyles(generalStyles, styles)
const useStyles = makeStyles(combinedStyles)

const TeamsFilterSection: React.FunctionComponent<any> = ({
	handleSetIndustryIds,
	handleSetLocationsIds,
	handleSetSize,
	handleSetAvgAge,
	handleSetCompanyId,
	toggleDrawer,
}) => {
	const theme = useTheme()
	const isSmDown: boolean = useMediaQuery(theme.breakpoints.down('sm'))
	const classes = useStyles()
	const { t } = useTranslation()
	const [inputLocation, setInputLocation] = useState<String>('')
	const [selectedLocations, setSelectedLoc] = useState<any[]>([])
	const [industryIds, setIndustryIds] = useState<String[]>([])
	const [teamSizeFilter, setTeamSizeFilter] = useState<any[]>([])
	const [avgAgeFilter, setAvgAgeFilter] = useState<any[]>([])
	const [openIndustriesList, setOpenIndustriesList] = useState(false)
	let companyIndustries: any = { industries: {} } // TODO: add an interface here.

	const ITEM_HEIGHT = 48
	const ITEM_PADDING_TOP = 8
	const MenuProps = {
		PaperProps: {
			style: {
				maxHeight: ITEM_HEIGHT * 10.5 + ITEM_PADDING_TOP,
				width: 250,
			},
		},
	}

	const { loading, data: locations } = useQuery(RoLocations, {
		skip: !inputLocation,
		fetchPolicy: 'cache-first',
		variables: {
			data: {
				name: inputLocation,
				emptyResult: true,
				limitRows: 10,
				type_in: [
					'Comună',
					'Oraş',
					'Municipiu',
					'National capital',
					'Sectoarele municipiului',
				],
			},
		},
	})

	const { data: companyIndustriesData } = useQuery(IndustriesQuery, {
		skip: companyIndustries.length > 0,
		fetchPolicy: 'cache-first',
	})
	companyIndustries = useMemo(
		() => companyIndustriesData || { industries: [] },
		[companyIndustriesData],
	)
	const filterByIndustry = useCallback(
		(event: React.ChangeEvent<{ value: unknown }>) => {
			const options: any = event.target.value as HTMLSelectElement
			setIndustryIds(options)
			handleSetIndustryIds(options)
		},
		[handleSetIndustryIds],
	)
	const filterByCompany = useCallback(
		(data: { value: string; label: string }) => {
			if (data && data.value) handleSetCompanyId(data.value)
			else handleSetCompanyId('')
		},
		[handleSetCompanyId],
	)

	const handleSearchChange = useCallback((event: any) => {
		if (event && event.target) {
			setInputLocation(event.target.value)
		} else setInputLocation('')
	}, [])

	const checkIfExist = useCallback(
		(location: any) => {
			const existInList = _.find(
				selectedLocations,
				(item: any) => item.id === location.id,
			)
			return existInList === undefined
		},
		[selectedLocations],
	)

	const select = useCallback(
		(location: any) => {
			if (checkIfExist(location)) {
				setSelectedLoc([...selectedLocations, location])
				setInputLocation('')
			}
			let selectedLocationsIds = [...selectedLocations, location].map(
				item => {
					return item.id
				},
			)
			handleSetLocationsIds(selectedLocationsIds)
		},
		[handleSetLocationsIds, checkIfExist, selectedLocations],
	)
	const deselect = (location: any) => {
		const filterList = _.filter(
			selectedLocations,
			(item: any) => item.id !== location.id,
		)
		setSelectedLoc(filterList)
	}

	const checkIfSelected = useCallback((item: any, array: any[]) => {
		const index = _.indexOf(array, item)
		return index === -1
	}, [])

	const updateTeamSizeList = (size: any) => {
		if (checkIfSelected(size, teamSizeFilter)) {
			setTeamSizeFilter([...teamSizeFilter, size])
			handleSetSize([...teamSizeFilter, size])
		} else {
			const newList = _.filter(
				teamSizeFilter,
				(item: any) => item !== size,
			)
			setTeamSizeFilter(newList)
			handleSetSize(newList)
		}
	}
	const updateAvgAgeList = (age: any) => {
		if (checkIfSelected(age, avgAgeFilter)) {
			setAvgAgeFilter([...avgAgeFilter, age])
			handleSetAvgAge([...avgAgeFilter, age])
		} else {
			const newList = _.filter(avgAgeFilter, (item: any) => item !== age)
			setAvgAgeFilter(newList)
			handleSetAvgAge(newList)
		}
	}

	const handleOpenIndustriesList = useCallback(
		(value: 'open' | 'close' | 'delete', event: MouseEvent) => {
			if (value === 'open') {
				setOpenIndustriesList(true)
			} else if (value === 'close') {
				setOpenIndustriesList(false)
			} else if (value === 'delete') {
				event.stopPropagation()
			}
		},
		[],
	)

	const handleDeselectIndustry = useCallback(
		(industryId: string) => {
			const newIds = industryIds.filter(val => val !== industryId)
			setIndustryIds(newIds)
			handleSetIndustryIds(newIds)
		},
		[industryIds, handleSetIndustryIds],
	)
	return (
		<Grid
			container
			direction={'column'}
			spacing={1}
			className={classes.filterWrapper}
		>
			<Grid item xs={12}>
				<div
					className={classNames(
						classes.searchSectionTitle,
						classes.searchSection,
					)}
				>
					{isSmDown && (
						<div>
							<ArrowBackIcon onClick={toggleDrawer(false)} />
						</div>
					)}
					<div>
						<Trans key="advancedSearchTitle">
							{t('Advanced')}&nbsp;
							<span className={classes.searchSectionTitleMain}>
								{t('Search-1')}
							</span>
						</Trans>
					</div>
				</div>
			</Grid>
			<Grid item xs={12}>
				<div className={classes.selectedLoc}>
					{selectedLocations.map((item: any) => (
						<Chip
							key={'selectedLoc' + item.name}
							variant="outlined"
							className={classes.chip}
							label={item.name}
							clickable={true}
							size={'small'}
							onDelete={() => deselect(item)}
							deleteIcon={<Close />}
						/>
					))}
				</div>
				<div className={classes.searchField}>
					<InputBase
						onChange={handleSearchChange}
						className={classes.input}
						classes={{
							root: classes.inputBase
						}}
						placeholder={t('Locations')}
						inputProps={{ 'aria-label': 'Locations' }}
						value={inputLocation}
					/>
					<IconButton
						className={classes.iconButton}
						aria-label="search"
					>
						<SearchIcon
							fontSize={'small'}
							style={{ color: '#929292' }}
						/>
					</IconButton>
				</div>
				<ClickAwayListener onClickAway={() => {}}>
					<Paper>
						{loading && 'Loading ...'}
						{locations &&
							locations.roLocations &&
							locations.roLocations.map(
								(item: any, index: number) => (
									<Chip
										key={`${item.name}_${index}`}
										variant="outlined"
										label={item.name}
										className={classes.chip}
										clickable={checkIfExist(item)}
										size={'small'}
										onClick={() =>
											!checkIfExist(item)
												? null
												: select(item)
										}
									/>
								),
							)}
						{locations &&
							locations.roLocations &&
							locations.roLocations.length === 0 &&
							inputLocation && (
								<div>{t('No locations found')}</div>
							)}
					</Paper>
				</ClickAwayListener>
			</Grid>
			<Grid item xs={12}>
				<FormControl style={{ width: '100%' }}>
					<InputLabel id="industry-mutiple-chip-label" className={classes.labelIndustry}>
						{t('Industry')}
					</InputLabel>
					<Select
						labelId="industry-mutiple-chip-label"
						id="industry-mutiple-chip"
						classes={{ icon: classes.selectIcon }}
						multiple
						IconComponent={ExpandMoreIcon}
						value={industryIds}
						onChange={filterByIndustry}
						input={<BootstrapInput />}
						open={openIndustriesList}
						onOpen={(event: any) =>
							handleOpenIndustriesList('open', event)
						}
						onClose={(event: any) =>
							handleOpenIndustriesList('close', event)
						}
						renderValue={(selected: any) => {
							let selectedLabels = companyIndustries.industries.filter(
								(item: any) => {
									return selected.indexOf(item.id) > -1
								},
							)
							return (
								<div className={classes.chips}>
									{(selectedLabels as any[]).map(
										(value: any, index: number) => {
											return (
												<Chip
													key={`${index}`}
													label={
														<>
															{value.key
																.charAt(0)
																.toUpperCase() +
																value.key.slice(
																	1,
																)}
														</>
													}
													className={classes.chip}
													onDelete={() =>
														handleDeselectIndustry(
															value.id,
														)
													}
													deleteIcon={
														<CloseIcon
															classes={{
																root:
																	classes.iconClose,
															}}
															fontSize={'small'}
														/>
													}
													onMouseDown={(event: any) =>
														handleOpenIndustriesList(
															'delete',
															event,
														)
													}
												/>
											)
										},
									)}
								</div>
							)
						}}
						MenuProps={MenuProps}
					>
						{companyIndustries.industries.length > 0 &&
							companyIndustries.industries.map(
								(item: any, index: number) => {
									return (
										<MenuItem
											key={`industry_${index}`}
											value={item.id}
											style={getStyles(
												item,
												item.key
													.charAt(0)
													.toUpperCase() +
													item.key.slice(1),
												theme,
											)}
										>
											{item.key.charAt(0).toUpperCase() +
												item.key.slice(1)}
										</MenuItem>
									)
								},
							)}
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={12} className={classes.mt10}>
				<CompanySearchSelect filterByCompany={filterByCompany}/>
			</Grid>
			<Grid item xs={12}>
				<div className={classes.labelText}>{t('Team Size')}</div>
				<div>
					{Object.keys(teamSize).map(item => {
						return (
							<Chip
								key={teamSize[item]}
								variant="outlined"
								className={`${classes.chip} ${
									!checkIfSelected(item, teamSizeFilter)
										? classes.chipActive
										: classes.chipInactive
								} `}
								label={teamSize[item]}
								size={'small'}
								clickable={true}
								onClick={() => updateTeamSizeList(item)}
							/>
						)
					})}
				</div>
			</Grid>

			<Grid item xs={12}>
				<div className={classes.labelText}>{t('Average Age')}</div>
				<div>
					{avgAge.map((item: string, index: number) => {
						return (
							<Chip
								key={`avgAge_${index}`}
								variant="outlined"
								className={`${classes.chip} ${
									!checkIfSelected(item, avgAgeFilter)
										? classes.chipActive
										: classes.chipInactive
								} `}
								label={item}
								size={'small'}
								clickable={true}
								onClick={() => updateAvgAgeList(item)}
							/>
						)
					})}
				</div>
			</Grid>
		</Grid>
	)
}

export default TeamsFilterSection
